import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../withTypes';
import { Status, STATUSES } from '@/types/store';
import {
  AssignmentDetail,
  AssignmentGroup,
  CreateProjectInput,
  Language,
  MutationCreateLanguageArgs,
  MutationTriggerMachineTranslationGenerationArgs,
  MutationUpdateProjectArgs,
  Project,
  StatusGroup,
  TriggerMachineTranslationGenerationResponse,
  TriggerMachineTranslationGenerationStatus,
} from '@/graphql/types';
import { ASSIGNMENT_GROUP, CREATE_LANGUAGE_MUTATION, CREATE_PROJECT_MUTATION, GET_PROJECTS, PROJECT_DETAIL_QUERY, TRIGGER_MACHINE_TRANSLATION_GENERATION_MUTATION, UPDATE_PROJECT_MUTATION } from '@/graphql/queries';
import { QUERY_ASSIGNMENT_DETAIL } from '@/graphql/queries/assignments';
import { graphqlMutation, graphqlRequest, addRejectedToast, showRejectedToast } from '../../api/graphqlClient';
import { showToast } from '@/utils/utils';
import { errorMessages } from '@/utils/errorMessages';
import { successMessages } from '@/utils/successMessages';
import { DistributeResponse } from '@/types/projects';

export interface ProjectState {
  projects: Project[];
  allProjectStatus: Status;
  projectStatus: Status;
  projectDetails: Project | null;
  currentAssignment?: AssignmentDetail;
  toggledLevelIds: number[];
  toggledModuleIds: number[];
  assignmentGroup: AssignmentGroup[];
  assignmentStatus: StatusGroup[];
  assignmentGroupStatus: Status;
  assignmentGroupRequestId?: string;
}

const initialState: ProjectState = {
  projects: [],
  allProjectStatus: STATUSES.IDLE,
  projectStatus: STATUSES.IDLE,
  projectDetails: null,
  currentAssignment: undefined,
  toggledLevelIds: [],
  toggledModuleIds: [],
  assignmentGroup: [],
  assignmentStatus: [],
  assignmentGroupStatus: STATUSES.IDLE,
  assignmentGroupRequestId: undefined,
};

interface FetchProjectDetail {
  projectId: number;
}

interface FetchAssignmentGroupInput {
  projectId: number;
  isAdminView: boolean;
  skipLoadingState?: boolean;
}

// TODO: Normalize data, really needed? Also add sortComparer if needed.
/* const projectsAdapter = createEntityAdapter<Project>();
  const initialState = projectsAdapter.getInitialState();
  type EntityType = EntityState<Project, number>;
  type for getProjectsQuery: <EntityType, void>
  use transformResponse to call projectsAdapter.setAll, addOne, etc.
  use Object.values(data.entities) inside the components to get the project list as array
  finally export selectors
  export const { selectAll: selectAllProjects } = projectsAdapter.getSelectors(selectProjectsData);
  */

/* THUNKS */
export const fetchProjectDetail = createAppAsyncThunk(
  'projects/fetchProjectDetail',
  async ({ projectId }: FetchProjectDetail) => {
    const response = await graphqlRequest<FetchProjectDetail, { projectDetail: Project }>(
      PROJECT_DETAIL_QUERY,
      { projectId },
    );

    return response.projectDetail;
  },
);

export const fetchAllProjectThunk = createAppAsyncThunk(
  'assignment/fetchAllProject',
  async () => {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    const response = await graphqlRequest<{}, { projects: Project[] }>(
      GET_PROJECTS,
    );
    return response.projects;
  },
);

export const fetchAssignmentDetails = createAppAsyncThunk(
  'assignment/fetchAssignmentDetails',
  async (assignment_id: number) => {
    const response = await graphqlRequest<{ assignment_id: number }, { assignmentDetails: AssignmentDetail }>(
      QUERY_ASSIGNMENT_DETAIL,
      { assignment_id },
      'network-only',
    );
    return response.assignmentDetails;
  },
);

export const updateProjectThunk = createAppAsyncThunk(
  'projects/updateProject',
  async (payload: MutationUpdateProjectArgs) => {
    const result = await graphqlMutation<MutationUpdateProjectArgs, { updateProject: Project }>(
      UPDATE_PROJECT_MUTATION,
      payload,
    );
    const { updateProject } = result;
    showToast(successMessages.projectChangesSaved, 'success');
    return updateProject;
  });

export const createLanguageThunk = createAppAsyncThunk(
  'projects/createLanguage',
  async (payload: MutationCreateLanguageArgs) => {
    const result = await graphqlMutation<MutationCreateLanguageArgs, { createLanguage: Language }>(
      CREATE_LANGUAGE_MUTATION,
      payload,
    );

    return result.createLanguage;
  },
);

export const createProjectThunk = createAppAsyncThunk(
  'projects/createProject',
  async (project: CreateProjectInput) => {
    const result = await graphqlMutation<{ project: CreateProjectInput }, { createProject: Project }>(
      CREATE_PROJECT_MUTATION,
      { project },
    );

    const { createProject } = result;
    showToast(successMessages.projectCreated, 'success');
    return createProject;
  },
);

export const triggerMachineTranslationGenerationThunk = createAppAsyncThunk(
  'projects/triggerMachineTranslationGeneration',
  async (payload: MutationTriggerMachineTranslationGenerationArgs) => {
    const result = await graphqlMutation<
      MutationTriggerMachineTranslationGenerationArgs,
      { triggerMachineTranslationGeneration: TriggerMachineTranslationGenerationResponse }
    >(
      TRIGGER_MACHINE_TRANSLATION_GENERATION_MUTATION,
      payload,
    );

    return result.triggerMachineTranslationGeneration;
  },
);

export const fetchAssignmentGroupThunk = createAppAsyncThunk(
  'projects/fetchAssignmentGroup',
  async ({ projectId, isAdminView }: FetchAssignmentGroupInput) => {
    const response = await graphqlRequest<{ projectId: number, isAdminView: boolean }, { assignmentGroup: { status_group: StatusGroup[], assignment_group: AssignmentGroup[] } }>(
      ASSIGNMENT_GROUP,
      {
        projectId,
        isAdminView,
      },
    );

    if (!response) {
      showToast(errorMessages.fetchingProjectDetails, 'error');
      throw new Error(errorMessages.fetchingProjectDetails);
    }
    return response.assignmentGroup;
  },
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    handleLevelToggle: (state, action: PayloadAction<number>) => {
      const levelId = action.payload;
      const _toggledLevelIds: number[] = state.toggledLevelIds.find(l => l === levelId)
        ? state.toggledLevelIds.filter(l => l !== levelId)
        : [...state.toggledLevelIds, levelId];
      state.toggledLevelIds = _toggledLevelIds;
    },
    handleModuleToggle: (state, action: PayloadAction<number>) => {
      const moduleId = action.payload;
      const _toggledModuleIds: number[] = state.toggledModuleIds.find(l => l === moduleId)
        ? state.toggledModuleIds.filter(l => l !== moduleId)
        : [...state.toggledModuleIds, moduleId];
      state.toggledModuleIds = _toggledModuleIds;
    },
    updateAssignmentGroup: (state, action: PayloadAction<AssignmentGroup[]>) => {
      state.assignmentGroup = action.payload;
    },
    updateDistributeResult: (state, action: PayloadAction<DistributeResponse[]>) => {
      const distributeResponse = action.payload;
      distributeResponse.forEach((data) => {
        state.assignmentGroup.forEach((level) => {
          level.modules?.forEach((module) => {
            module.assignments?.forEach((assignment) => {
              if (assignment.id === data.assignment_id) {
                assignment.user_id = data.user_id;
              }
            });
          });
        });
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProjectDetail.rejected, (state, action) => {
      state.projectStatus = STATUSES.REJECTED;
      state.projectDetails = initialState.projectDetails;
      showRejectedToast(action, errorMessages.fetchingProjectDetails);
    });
    builder.addCase(fetchAllProjectThunk.pending, (state) => {
      state.allProjectStatus = STATUSES.PENDING;
    });
    builder.addCase(fetchAllProjectThunk.fulfilled, (state, action) => {
      if (action.payload) {
        state.projects = action.payload;
        state.allProjectStatus = STATUSES.FULFILLED;
      }
    });
    builder.addCase(fetchAllProjectThunk.rejected, (state, action) => {
      state.projects = initialState.projects;
      // Reset project details on fetch all projects failure too
      state.projectDetails = initialState.projectDetails;
      showRejectedToast(action, errorMessages.fetchingProjects);
    });
    builder.addCase(fetchAssignmentDetails.fulfilled, (state, action) => {
      state.currentAssignment = action.payload;
    });
    builder.addCase(updateProjectThunk.rejected, (state, action) => {
      state.projectStatus = STATUSES.REJECTED;
      showRejectedToast(action, errorMessages.savingProjectChanges);
    });
    builder.addCase(triggerMachineTranslationGenerationThunk.fulfilled, (_, action) => {
      const messageByStatus: Record<TriggerMachineTranslationGenerationStatus, string> = {
        [TriggerMachineTranslationGenerationStatus.Started]: successMessages.aiAutoStarted,
        [TriggerMachineTranslationGenerationStatus.TrainingAlreadyInProgress]: errorMessages.aiAutoInProgress,
        [TriggerMachineTranslationGenerationStatus.MaxTrainingFrequencyExceeded]: errorMessages.aiAutoFrequencyExceeded,
      };

      showToast(
        messageByStatus[action.payload.status],
        action.payload.status === TriggerMachineTranslationGenerationStatus.Started ? 'success' : 'error',
      );
    });
    addRejectedToast(builder, triggerMachineTranslationGenerationThunk, errorMessages.aiAutoTriggerFailed);
    builder.addCase(createProjectThunk.rejected, (state) => {
      state.projectStatus = STATUSES.REJECTED;
    });
    builder.addCase(fetchAssignmentGroupThunk.pending, (state, action) => {
      state.assignmentGroupRequestId = action.meta.requestId;
      if (!action.meta.arg.skipLoadingState) {
        state.assignmentGroupStatus = STATUSES.PENDING;
      }
    });
    builder.addCase(fetchAssignmentGroupThunk.fulfilled, (state, action) => {
      if (state.assignmentGroupRequestId !== action.meta.requestId) {
        return;
      }

      if (action.payload) {
        state.assignmentGroup = action.payload.assignment_group;
        state.assignmentStatus = action.payload.status_group;
        state.assignmentGroupStatus = STATUSES.FULFILLED;
      }
    });
    builder.addCase(fetchAssignmentGroupThunk.rejected, (state, action) => {
      if (state.assignmentGroupRequestId !== action.meta.requestId) {
        return;
      }

      showRejectedToast(action, errorMessages.fetchingProjectDetails);
      if (!action.meta.arg.skipLoadingState) {
        state.assignmentGroupStatus = STATUSES.REJECTED;
      }
    });
    builder.addMatcher(isAnyOf(fetchProjectDetail.fulfilled, updateProjectThunk.fulfilled, createProjectThunk.fulfilled), (state, action) => {
      if (action.payload) {
        state.projectDetails = action.payload;
        state.projectStatus = STATUSES.FULFILLED;
      }
    });
  },
});

export const {
  handleLevelToggle,
  handleModuleToggle,
  updateAssignmentGroup,
  updateDistributeResult,
} = projectsSlice.actions;

export default projectsSlice.reducer;
